import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router';

import styles from './styles.module.css';
import CardsView from '../../components/CardsView';
import { labels } from '../../components/ChangeView';
import LocationCard from '../../components/LocationCard';
import SearchHeader from '../../components/SearchHeader';
import LocationsIcon from '@/assets/icons/location.svg?react';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import EmptyState from '@/components/EmptyState';
import MapView from '@/components/MapView';
import { socketsEntities } from '@/constants/navigationPaths';
import { useWebSocket } from '@/context/WebSocketContext';
import { PowerSite, useGetPowerSites } from '@/hooks/services/Locations';
import delay from '@/solaris-lens-common-lib/src/utils';

function SiteViewContainer() {
  const location = useLocation();
  const [selectedView, setSelectedView] = useState<
    (typeof labels)[keyof typeof labels]
  >(labels.card);
  const [searchValue, setSearchValue] = useState('');
  const [locations, setLocations] = useState<PowerSite[]>([]);
  const isMapView = selectedView === labels.map;

  const { setEntityData, socketData } = useWebSocket();

  const { data, isLoading } = useGetPowerSites();

  useEffect(() => {
    if (isLoading) return;

    delay(1000).then(() => {
      setEntityData({
        entityName: socketsEntities.globalView,
        entityId: '*'
      });
    });

    return () => {
      setEntityData({ entityName: '', entityId: '' });
    };
  }, [location.pathname, isLoading]);

  useEffect(() => {
    if (data?.data) {
      setLocations(data.data as PowerSite[]);
    }
  }, [data]);

  useEffect(() => {
    if (!socketData) return;

    setLocations(prev => {
      return prev.map(site =>
        site.id === socketData.id ? { ...site, ...socketData } : site
      );
    });
  }, [socketData]);

  const filteredLocations = useMemo(() => {
    if (!searchValue.trim()) {
      return locations;
    }

    const searchTerm = searchValue.toLowerCase().trim();
    return locations.filter((location: PowerSite) => {
      const name = location.name || '';
      const city = location.city || '';
      const state = location.state || '';

      return (
        name.toLowerCase().includes(searchTerm) ||
        city.toLowerCase().includes(searchTerm) ||
        state.toLowerCase().includes(searchTerm)
      );
    });
  }, [searchValue, locations]);

  const handleViewChange = (view: string) => {
    setSelectedView(view);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  if (isLoading) {
    return (
      <div className={styles.mainContainer}>
        <ContainerLoader />
      </div>
    );
  }

  if (!filteredLocations.length && !searchValue.trim()) {
    return (
      <EmptyState
        title="It seems you don't have any locations assigned yet."
        subtitle="Please contact your administrator."
      />
    );
  }

  return (
    <div className={styles.mainContainer}>
      <SearchHeader
        searchPlaceholder="Search by location or by city"
        searchValue={searchValue}
        searchOnChange={handleSearchChange}
        icon={isMapView ? undefined : LocationsIcon}
        title={isMapView ? undefined : 'Locations'}
        renderTabs={true}
        selected={selectedView}
        onClickView={handleViewChange}
        floatHeader={isMapView}
        resetSearch={() => setSearchValue('')}
        showCross={searchValue.length > 0}
      />
      {searchValue.trim() && !filteredLocations.length ? (
        <EmptyState
          title="No locations found"
          subtitle={`No results found for "${searchValue}". Try a different search term.`}
          icon={false}
        />
      ) : isMapView ? (
        <MapView icon={LocationsIcon} filteredLocations={filteredLocations} />
      ) : (
        <div className={styles.cardsViewContainer}>
          <CardsView>
            {filteredLocations.map(location => (
              <LocationCard
                key={location.id}
                id={location.id}
                title={location.name}
                subtitle={
                  location.city && location.state
                    ? `${location.city}, ${location.state}`
                    : location.city || location.state || 'No location'
                }
                value={location.genTotalMW}
                unit={location.unit}
              />
            ))}
          </CardsView>
        </div>
      )}
    </div>
  );
}
export default SiteViewContainer;
